import   React                ,
       { useState             ,
	     useEffect            ,
		 useRef               }  from 'react';

import   classNames              from 'classnames';

import   styles                  from './MediaSelector.module.scss';

import { Media                ,
         MediaCategory        }  from '../../../dataTypes/mediaManagment/types';
import { Video        }  from '../../../dataTypes/mediaManagment/video_types';
import { AudioSlideshow       }  from '../../../dataTypes/mediaManagment/audioSlideshow_types';

import   Select                  from '../../widget/inputs/Select';
import   Field                   from '../../widget/inputs/Field';
import   Trigger                 from '../../widget/actuators/Trigger';
import   Box                     from '../../widget/windows/Box';
import   MediaSheet              from './MediaSheet';



import { getMediaById             ,
         getMediaList              }  from '../../../backendServices/mediaManagment/api';

import { useTypedSelector     }  from '../../../store/useTypeSelector';
// import { fetchMediaList       ,
//          retrieveMediaById    }  from '../../../store/mediaManagment/actions';
// import { fetchMediaCategories }  from '../../../store/mediaCategoryManagment/actions';

import { MediaThumbnail } from './MediaThumbnail'

export interface MediaSelectorProps {
  mediaSet?        : Media[];
  mediaCategories? : MediaCategory[];
  onCancel?        : () => void | null;
  onSelect?        : (id: number) => void | null;
  thumbnailWidth?  : number;
  thumbnailHeight? : number;
  componentWidth?  : number;
  componentHeight? : number;
  originalFilter?  : string[];
  mediaTypeNames?  : string[];
  mediaTypeLabels? : string[];
}

// ✅ EN DEHORS du composant (avant export const MediaSelector)
const DEFAULT_ORIGINAL_FILTER: string[] = [];
const DEFAULT_MEDIA_TYPE_NAMES: string[] = [];

// Comparaison de contenu (identité des éléments) entre deux listes de médias.
const sameMediaList = (a: Media[], b: Media[]): boolean =>
  a.length === b.length && a.every((media, index) => media === b[index]);

export const MediaSelector = ({ mediaSet                                                            ,
                                mediaCategories                                                     ,
                                mediaTypeNames   : customMediaTypeNames  = DEFAULT_MEDIA_TYPE_NAMES ,
                                mediaTypeLabels  : customMediaTypeLabels                            ,
                                originalFilter                           = DEFAULT_ORIGINAL_FILTER  ,
                                onCancel                                                            ,
                                onSelect                                                            ,
                                thumbnailWidth                           = 300                      ,
                                thumbnailHeight                          = 170                      ,
                                componentWidth                           = 1210                     ,
                                componentHeight                          = 626                      }: MediaSelectorProps) => {


  // Même périmètre que les parents qui chargeaient la liste (ex.
  // BroadcastingChannelSheet, qui passe déjà l'admin connecté à getMediaList) :
  // le sélecteur ne doit pas se mettre à proposer les médias d'un autre.
  const authentication = useTypedSelector(state => state.authenticationReducer.authentication);

  const medias     = useTypedSelector(state => state.mediaListReducer.medias);
  const categories = useTypedSelector(state => state.mediaCategoryReducer.categories);


  const mediaArray    = Array.from(medias.values());
  const categoryArray = Array.from(categories.values());

  const [ mediaCategorySet        ] = useState<MediaCategory[]>(mediaCategories ?? categoryArray);
  const [ originalMediaSet        , setOriginalMediaSet        ] = useState<Media[]>(mediaSet ?? mediaArray);
  const [ filteredMediaSet        , setFilteredMediaSet        ] = useState<Media[]>(mediaSet ?? mediaArray);
  const [ selectedMediaType       , setSelectedMediaType       ] = useState(( customMediaTypeNames.length === 1 && !customMediaTypeNames.includes('Tous') ) ? customMediaTypeNames[0] : 'Tous');
  const [ selectedCategory        , setSelectedCategory        ] = useState('Toutes');
  const [ selectedSortingCriteria , setSelectedSortingCriteria ] = useState('Aucun tri');
  const [ filter                  , setFilter                  ] = useState('');
  const [ showCreateWindow        , setShowCreateWindow        ] = useState(false);

  const creationWindowRef = useRef<HTMLDivElement>(null);

  // ── Le sélecteur va chercher sa liste lui-même ────────────────────────────
  //
  // Il FILTRE sur `media.active` : un média désactivé doit disparaître d'ici, un
  // média activé doit y entrer. Mais la liste lui venait du parent, qui la charge
  // une seule fois à son montage (ainsi BroadcastingChannelSheet) — le sélecteur
  // travaillait donc sur une photo prise avant la modification, parfois vieille de
  // plusieurs écrans, et rien ne la rafraîchissait jamais. Le repli sur le store
  // Redux ne servait à rien non plus : `fetchMediaList` n'est dispatché nulle part.
  //
  // Et quand aucun `mediaSet` n'est fourni (SoundSheet, AudioSlideshowSheet),
  // l'effet ci-dessous n'avait rien à filtrer : le sélecteur s'affichait vide.
  //
  // `mediaSet` reste utile comme affichage IMMÉDIAT le temps de l'aller-retour :
  // sans lui, le sélecteur clignoterait vide à chaque ouverture. Une fois notre
  // liste arrivée, c'est elle qui fait foi — d'où le garde `listeChargeeRef`.
  const listeChargeeRef = useRef(false);

  const typesToFetch = React.useMemo(
    () => ( originalFilter.length === 0 || originalFilter.includes('Tous')
            ? ['Image', 'Sound', 'Video', 'AudioSlideshow']
            : originalFilter ),
    [originalFilter],
  );

  useEffect(() => {

    let annule = false;

    (async () => {

      // 'Tous' est le libellé de l'interface ; getMediaList attend une liste vide
      // (ou 'All') pour « tous les types ». Le lui passer tel quel ne ramènerait
      // rien, faute de fonction de chargement portant ce nom.
      const types = ( originalFilter.length === 0 || originalFilter.includes('Tous') )
                    ? []
                    : originalFilter;

      const reponse = await getMediaList(types, authentication.admin?.id);

      if (annule || reponse.code !== 1) return;

      const filtered = reponse.payload.filter((m) => typesToFetch.includes(m.mediaType));

      listeChargeeRef.current = true;
      setOriginalMediaSet(prev => sameMediaList(prev, filtered) ? prev : filtered);
    })();

    return () => { annule = true; };

  }, [originalFilter, typesToFetch, authentication.admin?.id]);

  useEffect(() => {

    // Notre propre liste a pris la main : le tableau du parent, lui, ne bouge plus.
    // Le laisser écraser ce qu'on vient de charger ramènerait l'état périmé.
    if (listeChargeeRef.current) return;

    const filtered = (mediaSet ?? []).filter((m) => typesToFetch.includes(m.mediaType));

    // Le parent recrée souvent le tableau mediaSet à l'identique (rafraîchissement
    // périodique) : on ne remplace l'état que si le contenu a réellement changé,
    // sinon chaque tick déclenche une cascade de re-rendus inutiles.
    setOriginalMediaSet(prev => sameMediaList(prev, filtered) ? prev : filtered);

  }, [typesToFetch, mediaSet, customMediaTypeNames]);

  useEffect(() => {
    filterMedias();
  }, [selectedMediaType, selectedCategory, selectedSortingCriteria, filter, originalMediaSet]);

  useEffect(() => {
    if (showCreateWindow && creationWindowRef.current)
	 {
      creationWindowRef.current.scrollTop = 0;
     }
  }, [showCreateWindow]);

  useEffect(() => {
    // Réinitialiser le tri si on passe sur Image avec un tri par durée actif
    if (selectedMediaType === 'Image' &&
        (selectedSortingCriteria === 'Par durée croissante' ||
         selectedSortingCriteria === 'Par durée décroissante')) {
      setSelectedSortingCriteria('Aucun tri');
    }
  }, [selectedMediaType]);

  useEffect(() => {

    // Réinitialiser la catégorie si elle n'est plus disponible pour ce type de média
    if (selectedMediaType !== 'Tous') {

      const availableCategories = mediaCategorySet.filter(
        (category) => category.mediaType === selectedMediaType
      );

      if (availableCategories.length === 0 ||
           (selectedCategory !== 'Toutes' &&
            !availableCategories.some(cat => cat.name === selectedCategory))) {
        setSelectedCategory('Toutes');
      }
    }
  }, [selectedMediaType, mediaCategorySet]);

  const handleClick = (mediaId: number) => {

    if (onSelect)
	 {
      onSelect(mediaId);
     }
  };

  const renderMediaThumbnailsList = (): JSX.Element => {
    const thumbnailsPerRow = Math.floor(componentWidth / thumbnailWidth);
    const rows: JSX.Element[] = [];
    for (let i = 0; i < filteredMediaSet.length; i += thumbnailsPerRow) {
      const rowMedias = filteredMediaSet.slice(i, i + thumbnailsPerRow);
      const emptyColumns = thumbnailsPerRow - rowMedias.length;
      rows.push(
        <div key={`row-${i}`} className={classNames(styles.row)}>
          {rowMedias.map((media) => (
            <div key={media.id} className={classNames(styles.column)}>
              <MediaThumbnail
                media={media}
                thumbnailWidth={thumbnailWidth}
                thumbnailHeight={thumbnailHeight}
                onMediaClick={handleClick}
              />
            </div>
          ))}
          {Array.from({ length: emptyColumns }).map((_, index) => (
            <div key={`empty-${i}-${index}`} className={classNames(styles.column)}>
              <MediaThumbnail
                thumbnailWidth={thumbnailWidth}
                thumbnailHeight={thumbnailHeight}
                onMediaClick={handleClick}
              />
            </div>
          ))}
        </div>,
      );
    }
    return <div className={classNames(styles.media_grid)}>{rows}</div>;
  };

  const getMediaDurationInSeconds = (media: Media): number => {
    if (media.mediaType === 'Video' && (media as Video).duration) {
      return getDurationInSeconds((media as Video).duration);
    } else if (media.mediaType === 'AudioSlideshow' && (media as AudioSlideshow).imageDisplayDuration) {
      return getDurationInSeconds((media as AudioSlideshow).imageDisplayDuration);
    }
    return 0;
  };

  const getDurationInSeconds = (durationString: string): number => {
    if (!durationString) return 0;
    const [hours, minutes, seconds] = durationString.split(':').map(Number);
    return (hours || 0) * 3600 + (minutes || 0) * 60 + (seconds || 0);
  };

// Catégories réellement proposables pour le type de média courant.
const getAvailableCategories = (): MediaCategory[] =>
  selectedMediaType === 'Tous'
    ? mediaCategorySet
    : mediaCategorySet.filter((category) => category.mediaType === selectedMediaType);

const filterMedias = () => {
  if (originalMediaSet.length > 0) {
    // La catégorie est résolue par son nom, jamais par l'indice de l'<option> :
    // le widget Select insère un placeholder en tête de liste, donc selectedIndex
    // est décalé de 1 par rapport aux catégories (bug du "décalage d'une catégorie").
    const selectedCategoryObject = selectedCategory === 'Toutes'
      ? undefined
      : getAvailableCategories().find((category) => category.name === selectedCategory)
        ?? mediaCategorySet.find((category) => category.name === selectedCategory);

    let filteredMedias = originalMediaSet.filter((media) => {
      const matchesMediaType = selectedMediaType === 'Tous' || media.mediaType === selectedMediaType;
      const matchesCategory = selectedCategory === 'Toutes' ||
        (selectedCategoryObject !== undefined && media.categoriesIds.includes(selectedCategoryObject.id));
      const matchesFilter = media.name.toLowerCase().includes(filter.toLowerCase());
      const matchesActive = media.active === true;
      return matchesMediaType && matchesCategory && matchesFilter && matchesActive;
    });

    // « Date de sortie » = releaseDate, propre aux vidéos : les autres types n'en
    // ont pas et retombent sur leur creationDate.
    const releaseTime  = (m: Media): number => ((m as Video).releaseDate ?? m.creationDate)?.getTime() || 0;
    const creationTime = (m: Media): number => m.creationDate?.getTime() || 0;

    if (selectedSortingCriteria === 'Par date de sortie croissante') {
      filteredMedias.sort((a, b) => releaseTime(a) - releaseTime(b));
    } else if (selectedSortingCriteria === 'Par date de sortie décroissante') {
      filteredMedias.sort((a, b) => releaseTime(b) - releaseTime(a));
    } else if (selectedSortingCriteria === 'Par durée croissante') {
      filteredMedias.sort((a, b) => getMediaDurationInSeconds(a) - getMediaDurationInSeconds(b));
    } else if (selectedSortingCriteria === 'Par durée décroissante') {
      filteredMedias.sort((a, b) => getMediaDurationInSeconds(b) - getMediaDurationInSeconds(a));
    } else {
      // « Aucun tri » : les plus récemment déposés en premier.
      filteredMedias.sort((a, b) => creationTime(b) - creationTime(a));
    }
    setFilteredMediaSet(prev => sameMediaList(prev, filteredMedias) ? prev : filteredMedias);
  }
};

  const handleMediaTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedMediaType(e.target.value);
  };

  const handleCategoryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedCategory(e.target.value);
  };

  const handleSortingCriteriaChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedSortingCriteria(e.target.value);
  };

  const handleFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFilter(e.target.value);
  };

  const renderMediaTypeSelection = (): JSX.Element | null => {
    if (customMediaTypeNames.length > 0) {
      let types = [...customMediaTypeNames];
      let labels = customMediaTypeLabels ? [...customMediaTypeLabels] : types;
      if (types.length > 1) {
        types.unshift('Tous');
        labels.unshift('Tous');
      }
      if (types.length === 1) {
        return null;
      }
      return (
        <Select
          choices={types}
          choicesLabel={labels}
          value={selectedMediaType}
          onChange={handleMediaTypeChange}
        />
      );
    }
    const defaultMediaTypeNames = ['Tous', 'Video', 'AudioSlideshow', 'Image', 'Sound'];
    const defaultMediaTypeLabels = ['Tous', 'Vidéo', 'Diapositive', 'Image', 'Son'];
    return (
      <Select
        choices={defaultMediaTypeNames}
        choicesLabel={defaultMediaTypeLabels}
        value={selectedMediaType}
        onChange={handleMediaTypeChange}
      />
    );
  };


  /*
const renderCategoriesSelection = (): JSX.Element => {
  // Filtrer les catégories selon le type de média sélectionné
  let filteredCategories = mediaCategorySet;

  if (selectedMediaType !== 'Tous') {
    filteredCategories = mediaCategorySet.filter(
      (category) => category.mediaType === selectedMediaType
    );
  }

  // Si aucune catégorie disponible
  if (filteredCategories.length === 0) {
    return (
      <Select
        choices={['Aucune catégorie']}
        choicesDisabled={[true]}
        value="Aucune catégorie"
        onChange={handleCategoryChange}
      />
    );
  }

  // Sinon, afficher le sélecteur normal
  const categoryNames = ['Toutes', ...filteredCategories.map((category) => category.name)];
  return (
    <Select
      choices={categoryNames}
      value={selectedCategory}
      onChange={handleCategoryChange}
    />
  );
};
*/

    const renderCategoriesSelection = (): JSX.Element => {
    // Mêmes catégories que celles utilisées par filterMedias : proposer une catégorie
    // d'un autre type de média ne pouvait donner qu'une grille vide.
    const categoryNames = ['Toutes', ...getAvailableCategories().map((category) => category.name)];
    return (
      <Select
        choices={categoryNames}
        value={selectedCategory}
        onChange={handleCategoryChange}
      />
    );
  };


const renderSortingCriteria = (): JSX.Element => {
  let sortingCriteriaNames: string[];

  // Pour les images, on exclut les tris par durée
  if (selectedMediaType === 'Image') {
    sortingCriteriaNames = [
      'Aucun tri',
      'Par date de sortie croissante',
      'Par date de sortie décroissante',
    ];
  } else {
    sortingCriteriaNames = [
      'Aucun tri',
      'Par date de sortie croissante',
      'Par date de sortie décroissante',
      'Par durée croissante',
      'Par durée décroissante',
    ];
  }

  return (
    <Select
      choices={sortingCriteriaNames}
      value={selectedSortingCriteria}
      onChange={handleSortingCriteriaChange}
    />
  );
};

  const handleCloseButtonClick = () => {
    if (onCancel) {
      onCancel();
    }
  };

  const handleCreationWindowShow = () => {
    setShowCreateWindow(true);
  };

  const handleCreationWindowCancel = () => {
    setShowCreateWindow(false);
  };


  const handleMediaCreate = async (id: number) => {
    setShowCreateWindow(false);

    // Le media venant d'être créé, on l'ajoute directement à la liste plutôt que de
    // dépendre du parent pour recharger mediaSet (qu'il ne recharge pas systématiquement).
    const result = await getMediaById(id);

    if (result.code === 1) {
      setOriginalMediaSet(prev => [...prev, result.payload]);
    }
  };

  return (
    <>
      {showCreateWindow && (
        <Box
          onClose={handleCreationWindowCancel}
          width="90vw"
          height="90vh"
          marginTop="0px"
          contentMargin="10px"
        >
		 {
		  selectedMediaType !== 'Tous'
		  ?
          <MediaSheet onCreate={handleMediaCreate} mediaType={selectedMediaType}/>
		  :
          <MediaSheet onCreate={handleMediaCreate}/>
		 }
        </Box>
      )}
      <Box
        onClose={handleCloseButtonClick}
        width={`${componentWidth + 70}px`}
        height={`${componentHeight}px`}
        marginTop="0px"
        contentMargin="10px"
        zIndex={9900}
      >
        <>
          <div className={classNames(styles.upper_bar)}>
            {renderMediaTypeSelection()}
            {renderCategoriesSelection()}
            {renderSortingCriteria()}
            <Field
              type="text"
              placeholder="Filtrer par titre"
              value={filter}
              onChange={handleFilterChange}
            />
            <div className={classNames(styles.mediaCreationButton)}>
              <Trigger
                name="Créer un media"
                onClick={handleCreationWindowShow}
                variant="tailwind2"
              />
            </div>
          </div>
          <div className={classNames(styles.media_grid)}>
            {renderMediaThumbnailsList()}
          </div>
        </>
      </Box>
    </>
  );
};

export default MediaSelector;
